<script setup>
import {Bars3Icon, XMarkIcon} from '@heroicons/vue/24/solid';
import {onClickOutside, useWindowScroll, useWindowSize} from '@vueuse/core';
import {computed, onMounted, ref, useTemplateRef, watch} from 'vue';

defineProps({
    menuItems: {
        type: Array,
        default: () => [],
    },
});

const {width: windowWidth, height: windowHeight} = useWindowSize();
const {y: scrollY} = useWindowScroll();

const isOpen = ref(false);
const activeSection = ref(null);
const menuButton = useTemplateRef('menuButton');

const activeSectionHash = computed(() => `#${activeSection.value}`);
const isMobile = computed(() => windowWidth.value < 768);
const isFixed = computed(() => scrollY.value >= (windowHeight.value - 72));
const fixedClasses = computed(() => isFixed.value ? ['w-full', 'fixed', 'top-0', 'z-50', 'bg-black'] : '');

function isActive(menuItem) {
    return menuItem.url === activeSectionHash.value;
}

onClickOutside(menuButton, () => isOpen.value = false);

watch(activeSectionHash, () => history.replaceState(null, null, activeSectionHash.value));

onMounted(() => {
    const sections = document.querySelectorAll('section');
    const observer = new IntersectionObserver(entries => {
        const activeEntry = entries.find(entry => entry.isIntersecting);
        if (activeEntry) {
            activeSection.value = activeEntry.target.id;
        }
    }, {threshold: 0.5});
    sections.forEach(target => observer.observe(target));
});
</script>

<template>
    <nav class="h-18 md:flex transition-colors duration-300 ease-in-out" :class="fixedClasses">
        <div class="h-full flex items-center justify-between bg-black relative z-10" v-show="isFixed">
            <a href="#home" class="inline-block -mt-1 ml-2 font-cursive text-6xl">
                ISA-L
            </a>
            <button type="button"
                    class="mr-2 p-2 md:hidden cursor-pointer"
                    @click="isOpen = !isOpen"
                    ref="menuButton">
                <Transition enter-active-class="transition duration-200 ease-in-out"
                            enter-from-class="opacity-0"
                            leave-active-class="transition duration-200 ease-in-out absolute"
                            leave-to-class="opacity-0">
                    <XMarkIcon class="size-8" v-if="isOpen"/>
                    <Bars3Icon class="size-8" v-else/>
                </Transition>
            </button>
        </div>
        <Transition enter-active-class="transition duration-300 ease-in-out"
                    enter-from-class="-translate-y-full"
                    leave-active-class="transition duration-300 ease-in-out"
                    leave-to-class="-translate-y-full">
            <div :class="isFixed ? 'ml-auto' : 'mx-auto'" v-show="(isOpen && isFixed) || !isMobile">
                <ul class="flex flex-col md:flex-row bg-gray-800 md:bg-transparent">
                    <li v-for="menuItem in menuItems">
                        <a :href="menuItem.url"
                           class="h-full flex items-center px-4 py-2 md:py-6 hover:bg-white hover:text-black transition-colors duration-300 ease-in-out"
                           :class="{'bg-white text-black': isActive(menuItem)}">
                            {{ menuItem.title }}
                        </a>
                    </li>
                </ul>
            </div>
        </Transition>
    </nav>
</template>
